import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { GripVertical, Plus, Pencil, Trash2, ExternalLink, Save, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SiteSettingsTab = ({ getAuthHeaders }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  
  // Form state for editing
  const [formData, setFormData] = useState({
    thesis_layer: 'efficiency',
    category: '',
    name: '',
    description: '',
    link_url: ''
  });

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/portfolio/companies`);
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const efficiencyCompanies = companies
    .filter(c => c.thesis_layer === 'efficiency')
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  
  const humanCompanies = companies
    .filter(c => c.thesis_layer === 'human')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const openAddModal = (thesisLayer) => {
    setEditingCompany(null);
    setFormData({
      thesis_layer: thesisLayer,
      category: '',
      name: '',
      description: '',
      link_url: ''
    });
    setShowEditModal(true);
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setFormData({
      thesis_layer: company.thesis_layer,
      category: company.category,
      name: company.name,
      description: company.description,
      link_url: company.link_url || ''
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    const authHeaders = getAuthHeaders();
    setSaving(true);
    
    try {
      const url = editingCompany 
        ? `${BACKEND_URL}/api/portfolio/companies/${editingCompany.company_id}`
        : `${BACKEND_URL}/api/portfolio/companies`;
      
      const method = editingCompany ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setShowEditModal(false);
        fetchCompanies();
      }
    } catch (error) {
      console.error('Failed to save company:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Delete "${company.name}"?`)) return;
    
    const authHeaders = getAuthHeaders();
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/portfolio/companies/${company.company_id}`,
        {
          method: 'DELETE',
          headers: authHeaders,
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        fetchCompanies();
      }
    } catch (error) {
      console.error('Failed to delete company:', error);
    }
  };

  const handleDragStart = (e, company) => {
    setDraggedItem(company);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, company) => {
    e.preventDefault();
    if (draggedItem && draggedItem.thesis_layer === company.thesis_layer) {
      setDragOverItem(company);
    }
  };

  const handleDragEnd = async () => {
    if (!draggedItem || !dragOverItem || draggedItem.company_id === dragOverItem.company_id) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const thesisLayer = draggedItem.thesis_layer;
    const layerCompanies = thesisLayer === 'efficiency' ? [...efficiencyCompanies] : [...humanCompanies];
    
    const draggedIndex = layerCompanies.findIndex(c => c.company_id === draggedItem.company_id);
    const targetIndex = layerCompanies.findIndex(c => c.company_id === dragOverItem.company_id);
    
    // Reorder array
    const [removed] = layerCompanies.splice(draggedIndex, 1);
    layerCompanies.splice(targetIndex, 0, removed);
    
    // Update order in backend
    const authHeaders = getAuthHeaders();
    try {
      await fetch(`${BACKEND_URL}/api/portfolio/companies/reorder/${thesisLayer}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          company_ids: layerCompanies.map(c => c.company_id)
        })
      });
      fetchCompanies();
    } catch (error) {
      console.error('Failed to reorder companies:', error);
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  const renderCompanyCard = (company, index) => {
    const isDragging = draggedItem?.company_id === company.company_id;
    const isDragOver = dragOverItem?.company_id === company.company_id;
    const isEfficiency = company.thesis_layer === 'efficiency';
    
    return (
      <div
        key={company.company_id}
        draggable
        onDragStart={(e) => handleDragStart(e, company)}
        onDragOver={(e) => handleDragOver(e, company)}
        onDragEnd={handleDragEnd}
        className={`group relative p-6 rounded-xl backdrop-blur-xl bg-white/[0.03] border transition-all duration-200 cursor-move ${
          isDragging ? 'opacity-50 border-[#00F0FF]/50' : 
          isDragOver ? 'border-[#00F0FF] bg-[#00F0FF]/5' :
          'border-white/10 hover:border-white/20'
        }`}
      >
        {/* Drag handle */}
        <div className="absolute left-2 top-1/2 -translate-y-1/2 opacity-30 group-hover:opacity-100 transition-opacity">
          <GripVertical className="w-4 h-4 text-[#6B7280]" />
        </div>
        
        {/* Actions */}
        <div className="absolute right-3 top-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => openEditModal(company)}
            className="p-1.5 rounded hover:bg-white/10 text-[#6B7280] hover:text-[#00F0FF] transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => handleDelete(company)}
            className="p-1.5 rounded hover:bg-red-500/10 text-[#6B7280] hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="pl-4">
          <span className={`inline-block font-mono text-[10px] tracking-[0.3em] border px-2 py-1 rounded-sm mb-3 ${
            isEfficiency ? 'text-[#00F0FF] border-[#00F0FF]/30' : 'text-orange-400 border-orange-400/30'
          }`}>
            {company.category}
          </span>
          
          <h4 className="font-mono text-lg text-[#F3F4F6] tracking-wide mb-2 flex items-center gap-2">
            {company.name}
            {company.link_url && (
              <ExternalLink className="w-3.5 h-3.5 text-[#6B7280]" />
            )}
          </h4>
          
          <p className="font-sans text-[#6B7280] text-sm leading-relaxed line-clamp-2">
            {company.description}
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-white/10 rounded w-48"></div>
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="h-32 bg-white/5 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* The Efficiency Layer */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-mono text-lg text-[#F3F4F6] flex items-center gap-2">
            <span className="text-[#00F0FF]">The Efficiency Layer</span>
            <span className="text-xs text-[#6B7280]">({efficiencyCompanies.length} companies)</span>
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openAddModal('efficiency')}
            className="border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Company
          </Button>
        </div>
        
        <div className="space-y-3">
          {efficiencyCompanies.map((company, index) => renderCompanyCard(company, index))}
          {efficiencyCompanies.length === 0 && (
            <div className="text-center py-8 text-[#6B7280] border border-dashed border-white/10 rounded-xl">
              No companies in this section
            </div>
          )}
        </div>
      </div>

      {/* The Human Layer */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-mono text-lg text-[#F3F4F6] flex items-center gap-2">
            <span className="text-orange-400">The Human Layer</span>
            <span className="text-xs text-[#6B7280]">({humanCompanies.length} companies)</span>
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openAddModal('human')}
            className="border-orange-400/30 text-orange-400 hover:bg-orange-400/10"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Company
          </Button>
        </div>
        
        <div className="space-y-3">
          {humanCompanies.map((company, index) => renderCompanyCard(company, index))}
          {humanCompanies.length === 0 && (
            <div className="text-center py-8 text-[#6B7280] border border-dashed border-white/10 rounded-xl">
              No companies in this section
            </div>
          )}
        </div>
      </div>

      {/* Edit/Add Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg bg-[#0a0a0a] border-white/10 text-[#F3F4F6]">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {editingCompany ? 'Edit Company' : 'Add Company'}
            </DialogTitle>
            <DialogDescription className="text-[#6B7280]">
              {editingCompany ? 'Update portfolio company details' : 'Add a new portfolio company'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Thesis Layer */}
            <div className="space-y-2">
              <label className="text-sm text-[#6B7280]">Thesis Section</label>
              <Select
                value={formData.thesis_layer}
                onValueChange={(value) => setFormData({ ...formData, thesis_layer: value })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-[#F3F4F6]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10">
                  <SelectItem value="efficiency" className="text-[#F3F4F6]">
                    <span className="text-[#00F0FF]">The Efficiency Layer</span>
                  </SelectItem>
                  <SelectItem value="human" className="text-[#F3F4F6]">
                    <span className="text-orange-400">The Human Layer</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="text-sm text-[#6B7280]">Category (shown in blue box, all caps)</label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g. INFRASTRUCTURE, LEGACY"
                className="bg-white/5 border-white/10 text-[#F3F4F6] placeholder:text-[#6B7280]/50"
              />
            </div>

            {/* Name */}
            <div className="space-y-2">
              <label className="text-sm text-[#6B7280]">Company Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. TrueContext.AI"
                className="bg-white/5 border-white/10 text-[#F3F4F6] placeholder:text-[#6B7280]/50"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm text-[#6B7280]">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Multi-line description of the company..."
                rows={3}
                className="bg-white/5 border-white/10 text-[#F3F4F6] placeholder:text-[#6B7280]/50 resize-none"
              />
            </div>

            {/* Link URL */}
            <div className="space-y-2">
              <label className="text-sm text-[#6B7280]">Link URL (optional - opens in new tab)</label>
              <Input
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                placeholder="https://example.com"
                className="bg-white/5 border-white/10 text-[#F3F4F6] placeholder:text-[#6B7280]/50"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={() => setShowEditModal(false)}
              className="text-[#6B7280]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.category || !formData.name || !formData.description}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black"
            >
              {saving ? 'Saving...' : (editingCompany ? 'Update' : 'Add Company')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiteSettingsTab;
